import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { API } from "../App";
import "../components/sojourns/sojourns.css";
import JsonLd from "../components/JsonLd";
import { useCountdown, useSojournsChrome } from "../components/sojourns/primitives";
import { Prose, NoteHeader, Faq, faqJsonLd, NotesList } from "../components/sojourns/NoteChrome";
import { FollowLink, Follow } from "../components/sojourns/Follow";
import { noteBySlug, noteUrl, noteHero, noteCard, NOTES_DATE, SOJOURNS_ORIGIN, SOJOURNS_URL } from "../content/sojournsNotes";

const FONTS = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Mulish:wght@400;600&family=Space+Mono&family=Cinzel&display=swap";

const Facts = ({ edition }) => (
  <div className="sj-ticket sj-note-facts" data-testid="sojourns-note-facts">
    <div className="sj-ticket-head">
      <p className="sj-label">In brief</p>
      <p className="sj-ticket-no">Sojourn <b>No. {edition}</b></p>
    </div>
    <dl className="sj-pass">
      <div className="sj-cell"><dt>Length</dt><dd>twenty-eight nights, spring 2027.</dd></div>
      <div className="sj-cell"><dt>People</dt><dd>twelve, by application.</dd></div>
      <div className="sj-cell"><dt>Where</dt><dd>one house by the sea, told to accepted applicants.</dd></div>
      <div className="sj-cell"><dt>On board</dt><dd>three business mentors, a wellness facilitator, a holistic and mental wellbeing coach and a chef.</dd></div>
      <div className="sj-cell"><dt>Your business</dt><dd>keeps running, from a desk in the house.</dd></div>
      <div className="sj-cell"><dt>Fare</dt><dd>on application.</dd></div>
    </dl>
  </div>
);

const Section = ({ s }) => (
  <>
    <h2 className="sj-note-h2">{s.h}</h2>
    {s.p?.map((t, i) => <p className="sj-p" key={i}><Prose text={t} /></p>)}
    {s.list && <ul className="sj-note-list">{s.list.map((t, i) => <li key={i}><Prose text={t} /></li>)}</ul>}
  </>
);

export default function SojournsNotePage() {
  const { slug } = useParams();
  const note = noteBySlug(slug);
  const [settings, setSettings] = useState({ reveal_date: null, edition: "001" });
  const days = useCountdown(settings.reveal_date);
  const open = days !== null && days <= 0;
  useSojournsChrome();

  useEffect(() => {
    axios.get(`${API}/sojourns/settings`).then((r) => setSettings(r.data || {})).catch(() => {});
  }, []);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  const ld = useMemo(() => {
    if (!note) return null;
    const url = noteUrl(note.slug);
    const image = noteCard(note.slug);
    return {
      article: {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: note.title,
        description: note.description,
        image,
        author: { "@type": "Person", name: "Steve Haynes", url: "https://www.savicoliving.com/story" },
        publisher: { "@type": "Organization", name: "Savi Coliving", url: SOJOURNS_ORIGIN, logo: { "@type": "ImageObject", url: `${SOJOURNS_ORIGIN}/icon-512.png` } },
        mainEntityOfPage: url,
        about: { "@type": "Event", name: "Savi Sojourns No. 001", url: SOJOURNS_URL },
        datePublished: NOTES_DATE,
        dateModified: NOTES_DATE,
        inLanguage: "en-GB",
      },
      crumbs: {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Savi Coliving", item: `${SOJOURNS_ORIGIN}/` },
          { "@type": "ListItem", position: 2, name: "Savi Sojourns", item: SOJOURNS_URL },
          { "@type": "ListItem", position: 3, name: note.title, item: url },
        ],
      },
      faq: faqJsonLd(note.faq),
    };
  }, [note]);

  if (!note) return <Navigate to="/sojourns" replace />;
  const url = noteUrl(note.slug);
  const card = noteCard(note.slug);
  const edition = settings.edition || "001";

  return (
    <div className="sj arrived skip" data-testid="sojourns-note-page" data-slug={note.slug}>
      <Helmet title={note.metaTitle}>
        <meta name="description" content={note.description} />
        <link rel="canonical" href={url} />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Savi Sojourns" />
        <meta property="og:title" content={note.title} />
        <meta property="og:description" content={note.description} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={card} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={note.imageAlt} />
        <meta property="article:published_time" content={NOTES_DATE} />
        <meta property="article:author" content="Steve Haynes" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={note.title} />
        <meta name="twitter:description" content={note.description} />
        <meta name="twitter:image" content={card} />
        <meta name="theme-color" content="#12100E" />
        <link rel="icon" href="/sojourn-media/sojourns-favicon.ico?v=1" sizes="16x16 32x32 48x48" />
        <link rel="icon" type="image/png" sizes="32x32" href="/sojourn-media/sojourns-favicon-32.png?v=1" />
        <link rel="apple-touch-icon" sizes="180x180" href="/sojourn-media/sojourns-apple-touch-icon.png?v=1" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="stylesheet" href={FONTS} />
      </Helmet>
      <JsonLd id="sojourns-note-article" data={ld.article} />
      <JsonLd id="sojourns-note-crumbs" data={ld.crumbs} />
      <JsonLd id="sojourns-note-faq" data={ld.faq} />

      <NoteHeader open={open} />

      <article className="sj-note">
        <div className="sj-col">
          <header className="sj-note-head">
            <p className="sj-label" data-testid="sojourns-note-eyebrow">Savi Sojourns · Notes · {note.n}</p>
            <h1 className="sj-note-title" data-testid="sojourns-note-title">{note.title}</h1>
            <p className="sj-note-lede" data-testid="sojourns-note-lede">{note.lede}</p>
            <div className="sj-rule left" aria-hidden="true" />
            <p className="sj-note-meta">Steve Haynes · {note.minutes} min</p>
          </header>
        </div>

        <figure className="sj-col wide sj-still sj-note-still" data-testid="sojourns-note-still">
          <div className="sj-still-frame">
            <img
              src={noteHero(note.slug, 1600)}
              srcSet={`${noteHero(note.slug, 800)} 800w, ${noteHero(note.slug, 1600)} 1600w`}
              sizes="(min-width: 900px) 800px, 100vw"
              width="1600"
              height="1067"
              alt={note.imageAlt}
              fetchpriority="high"
              decoding="async"
            />
          </div>
        </figure>

        <div className="sj-col">
          {note.sections.map((s) => <Section s={s} key={s.h} />)}

          <Facts edition={edition} />

          <h2 className="sj-note-h2">Questions</h2>
          <Faq items={note.faq} testid="sojourns-note-faq" />

          <div className="sj-note-cta" data-testid="sojourns-note-cta">
            <p className="sj-label">Sojourn No. {edition}</p>
            <p>{open ? "The gate is open. Twelve places, by application." : "Applications open on 24 November 2026, with the full moon. Register and you'll hear first."}</p>
            <Link to="/sojourns#register" className="sj-btn link" data-testid="sojourns-note-register">{open ? "Apply now" : "Register interest"}</Link>
            <Follow quiet line="Most of what there is to know will appear on Instagram first. Keep an eye on it." testid="sojourns-note-follow" />
          </div>
        </div>

        <aside className="sj-col wide sj-note-more" data-testid="sojourns-note-more">
          <p className="sj-label">More notes</p>
          <NotesList except={note.slug} testid="sojourns-note-list" />
          <p className="sj-contact">
            Steve Haynes, Savi · <a href="mailto:hola@savicoliving.com">hola@savicoliving.com</a> · <FollowLink testid="sojourns-note-instagram" /> · <Link to="/sojourns">Savi Sojourns</Link> · <Link to="/sojourns/notes">Notes</Link> · <a href="https://www.savicoliving.com">savicoliving.com</a>
          </p>
        </aside>
      </article>
    </div>
  );
}
